<script setup>
import { watch, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useKeyboardEvents } from 'dashboard/composables/useKeyboardEvents';
import { useStore, useMapGetter } from 'dashboard/composables/store';

import Editor from 'dashboard/components-next/Editor/Editor.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ContactNoteItem from 'next/Contacts/ContactsSidebar/components/ContactNoteItem.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';

const props = defineProps({
  contactId: { type: [String, Number], required: true },
});

const { t } = useI18n();
const store = useStore();
const currentUser = useMapGetter('getCurrentUser');
const uiFlags = useMapGetter('contactNotes/getUIFlags');
const notesByContact = useMapGetter('contactNotes/getAllNotesByContactId');
const isFetchingNotes = computed(() => uiFlags.value.isFetching);
const isCreatingNote = computed(() => uiFlags.value.isCreating);
const contactId = computed(() => props.contactId);
const noteContent = ref('');
const shouldShowCreateModal = ref(false);
const notes = computed(() => {
  if (!contactId.value) {
    return [];
  }
  return notesByContact.value(contactId.value) || [];
});

const getWrittenBy = ({ user } = {}) => {
  const currentUserId = currentUser.value?.id;
  return user?.id === currentUserId
    ? t('CONTACTS_LAYOUT.SIDEBAR.NOTES.YOU')
    : user?.name || t('CONVERSATION.BOT');
};

const openCreateModal = () => {
  if (!contactId.value) {
    return;
  }

  noteContent.value = '';
  shouldShowCreateModal.value = true;
};

const closeCreateModal = () => {
  shouldShowCreateModal.value = false;
  noteContent.value = '';
};

const onAdd = async () => {
  if (!contactId.value || !noteContent.value || isCreatingNote.value) {
    return;
  }

  await store.dispatch('contactNotes/create', {
    content: noteContent.value,
    contactId: contactId.value,
  });
  noteContent.value = '';
  closeCreateModal();
};

const onDelete = noteId => {
  if (!contactId.value || !noteId) {
    return;
  }

  store.dispatch('contactNotes/delete', {
    noteId,
    contactId: contactId.value,
  });
};

const keyboardEvents = {
  '$mod+Enter': {
    action: onAdd,
    allowOnFocusedInput: true,
  },
};

useKeyboardEvents(keyboardEvents);

watch(
  contactId,
  newContactId => {
    closeCreateModal();
    if (newContactId) {
      store.dispatch('contactNotes/get', { contactId: newContactId });
    }
  },
  { immediate: true }
);
</script>

<template>
  <div class="flex flex-col gap-3 px-3 py-3">
    <div class="flex items-center justify-between gap-3">
      <div class="min-w-0">
        <h4 class="mb-0 text-sm font-semibold text-n-slate-12">
          {{ t('CONVERSATION.CRM.NOTES_TITLE') }}
        </h4>
        <p class="mb-0 mt-0.5 text-xs text-n-slate-10">
          {{ t('CONVERSATION.CRM.NOTES_DESCRIPTION') }}
        </p>
      </div>
      <NextButton
        xs
        ghost
        slate
        icon="i-lucide-plus"
        :label="t('CONVERSATION.CRM.ADD_NOTE_INLINE')"
        :disabled="!contactId || isFetchingNotes || shouldShowCreateModal"
        @click="openCreateModal"
      />
    </div>

    <div
      v-if="shouldShowCreateModal"
      class="flex flex-col gap-3 rounded-lg border border-n-weak p-3"
    >
      <Editor
        v-model="noteContent"
        focus-on-mount
        :placeholder="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.PLACEHOLDER')"
        class="[&>div]:!border-transparent [&>div]:px-3 [&>div]:py-3"
      />
      <div class="flex items-center justify-end gap-2">
        <NextButton
          ghost
          slate
          sm
          :label="t('CONVERSATION.CRM.CANCEL_NOTE')"
          :disabled="isCreatingNote"
          @click="closeCreateModal"
        />
        <NextButton
          solid
          blue
          sm
          icon="i-lucide-send"
          :label="t('CONTACTS_LAYOUT.SIDEBAR.NOTES.SAVE')"
          :is-loading="isCreatingNote"
          :disabled="!noteContent || isCreatingNote"
          @click="onAdd"
        />
      </div>
    </div>

    <div
      v-if="isFetchingNotes"
      class="flex items-center justify-center rounded-lg border border-n-weak py-8 text-n-slate-11"
    >
      <Spinner />
    </div>
    <div
      v-else-if="notes.length"
      class="flex max-h-[320px] flex-col overflow-y-auto rounded-lg border border-n-weak"
    >
      <ContactNoteItem
        v-for="note in notes"
        :key="note.id"
        class="px-4 py-4 last-of-type:border-b-0"
        :note="note"
        :written-by="getWrittenBy(note)"
        allow-delete
        collapsible
        @delete="onDelete"
      />
    </div>
    <p
      v-else
      class="mb-0 rounded-lg border border-dashed border-n-weak px-4 py-6 text-center text-sm leading-6 text-n-slate-11"
    >
      {{ t('CONVERSATION.CRM.EMPTY_NOTES') }}
    </p>
  </div>
</template>
